import supabase from '../supabase/supabase';

/**
 * Safely extract the response body from a Supabase Edge Function error.
 */
const getErrorResponse = async (error) => {
  const context = error?.context;

  if (!context) {
    return null;
  }

  // Supabase FunctionsHttpError normally exposes a Response object.
  if (typeof context.json === 'function') {
    try {
      return await context.json();
    } catch {
      // Response body may already have been consumed or may not be JSON.
    }
  }

  return null;
};

/**
 * Convert a Supabase Edge Function error into a normal Error
 * while preserving the backend error code and HTTP status.
 */
const getFunctionError = async (error) => {
  const response = await getErrorResponse(error);

  const message =
    response?.error ||
    response?.message ||
    error?.message ||
    'Unable to start checkout.';

  const functionError = new Error(message);

  functionError.code = response?.code || null;
  functionError.status = error?.context?.status || null;

  return functionError;
};

const validateOrganizationId = (organizationId) => {
  if (!organizationId || typeof organizationId !== 'string') {
    throw new Error('No active organization is selected.');
  }

  return organizationId.trim();
};

const validateBillingInterval = (billingInterval) => {
  if (billingInterval !== 'month' && billingInterval !== 'year') {
    throw new Error(
      'Invalid billing interval. Expected "month" or "year".'
    );
  }

  return billingInterval;
};

export const subscriptionService = {
  /**
   * Get all active ProfitYO subscription plans.
   *
   * Source of truth:
   * accounting.subscription_plans
   */
  async getSubscriptionPlans() {
    const { data, error } = await supabase
      .schema('accounting')
      .from('subscription_plans')
      .select(
        [
          'id',
          'name',
          'description',
          'monthly_price',
          'yearly_price',
          'is_free',
          'is_active'
        ].join(',')
      )
      .eq('is_active', true)
      .order('monthly_price', {
        ascending: true,
        nullsFirst: true
      });

    if (error) {
      throw error;
    }

    return data || [];
  },

  /**
   * Get the subscription belonging to the current organization.
   *
   * Source of truth:
   * accounting.organization_subscriptions
   */
  async getOrganizationSubscription(organizationId) {
    const validOrganizationId = validateOrganizationId(organizationId);

    const { data, error } = await supabase
      .schema('accounting')
      .from('organization_subscriptions')
      .select(
        [
          'id',
          'organization_id',
          'stripe_customer_id',
          'stripe_subscription_id',
          'stripe_price_id',
          'plan_name',
          'billing_interval',
          'status',
          'current_period_start',
          'current_period_end',
          'cancel_at_period_end',
          'canceled_at',
          'trial_start',
          'trial_end',
          'created_at',
          'updated_at'
        ].join(',')
      )
      .eq('organization_id', validOrganizationId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data || null;
  },

  /**
   * Start a Stripe Checkout Session.
   *
   * IMPORTANT:
   * The frontend sends planId and billingInterval only.
   *
   * The Edge Function is responsible for:
   * - validating organization membership
   * - validating the plan
   * - selecting the Stripe price ID
   * - creating the Stripe Checkout Session
   * - creating/updating the subscription placeholder
   *
   * The frontend never decides the Stripe price ID.
   */
  async createCheckoutSession({
    organizationId,
    planId,
    billingInterval
  }) {
    const validOrganizationId = validateOrganizationId(organizationId);

    if (!planId || typeof planId !== 'string') {
      throw new Error('A subscription plan is required.');
    }

    const validBillingInterval =
      validateBillingInterval(billingInterval);

    const { data, error } = await supabase.functions.invoke(
      'create-checkout-session',
      {
        body: {
          organizationId: validOrganizationId,
          planId: planId.trim(),
          billingInterval: validBillingInterval
        }
      }
    );

    if (error) {
      throw await getFunctionError(error);
    }

    if (!data?.success) {
      const responseError = new Error(
        data?.error || 'Checkout could not be started.'
      );

      responseError.code = data?.code || null;
      responseError.status = data?.status || null;

      throw responseError;
    }

    if (!data?.checkoutUrl) {
      const responseError = new Error(
        'Checkout session was created, but no checkout URL was returned.'
      );

      responseError.code = 'CHECKOUT_URL_MISSING';
      responseError.status = 500;

      throw responseError;
    }

    return data;
  }
};